// ===========================================
// COMPONENTE: RegistrarVEPDialog
// ===========================================
// Dialog para registrar VEP de un vencimiento

import { useState } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Receipt, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import {
  useRegistrarVEPForm,
  getFieldError,
  type RegistrarVEPFormValues,
} from './forms'
import type { RegistrarVEPData } from '@/types/vencimientos'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface VencimientoParaVEP {
  id: string | null
  tipo_impuesto: string | null
  cliente_nombre: string | null
  periodo: string | null
  fecha_vencimiento: string | null
  monto_determinado?: number | null
}

interface RegistrarVEPDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vencimiento: VencimientoParaVEP | null
  onConfirmar: (id: string, datos: RegistrarVEPData) => Promise<void>
}

export function RegistrarVEPDialog({
  open,
  onOpenChange,
  vencimiento,
  onConfirmar,
}: RegistrarVEPDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { form } = useRegistrarVEPForm({
    onSubmit: async (value: RegistrarVEPFormValues) => {
      if (!vencimiento?.id) return

      setIsSubmitting(true)
      try {
        await onConfirmar(vencimiento.id, {
          vep_numero: value.vep_numero,
          vep_monto: value.vep_monto,
          vep_fecha_vencimiento: value.vep_fecha_vencimiento,
        })
        form.reset()
        onOpenChange(false)
      } catch (error) {
        console.error('Error al registrar VEP:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
    defaultValues: {
      vep_monto: vencimiento?.monto_determinado != null && vencimiento.monto_determinado > 0
        ? vencimiento.monto_determinado
        : null,
    },
  })

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) form.reset()
    onOpenChange(newOpen)
  }

  if (!vencimiento) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)} className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-amber-600" />
            Registrar VEP
          </DialogTitle>
          <DialogDescription>
            Registrar Volante Electrónico de Pago
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            {vencimiento.tipo_impuesto && (
              <Badge variant="outline" className={getImpuestoColorClasses(vencimiento.tipo_impuesto)}>
                {vencimiento.tipo_impuesto}
              </Badge>
            )}
            <span className="text-sm font-medium">{vencimiento.cliente_nombre}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            <span>Periodo: {vencimiento.periodo}</span>
            {vencimiento.fecha_vencimiento && (
              <>
                <span className="mx-2">|</span>
                <span>Vencimiento: {format(new Date(vencimiento.fecha_vencimiento), 'dd/MM/yyyy')}</span>
              </>
            )}
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="vep_numero">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Número de VEP *</Label>
                <Input
                  id={field.name}
                  placeholder="Ej: 12345678901234"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="vep_monto">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Monto del VEP</Label>
                <Input
                  id={field.name}
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  value={field.state.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.handleChange(val === '' ? null : parseFloat(val))
                  }}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="vep_fecha_vencimiento">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Fecha de Vencimiento del VEP</Label>
                <Input
                  id={field.name}
                  type="date"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrar VEP
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
